/**
 * Types for the account's reusable library of images and reference files.
 *
 * These live beside the queries rather than in the SDK models file because the library has no view model
 * at all: its state is entirely react-query. `imageLibraryItemToAttachment` in `useImageLibraryQuery` is
 * the seam that keeps them from rippling outward, converting an entry to the shared `IFileAttachment`
 * every picker consumer already expects.
 */
export enum ImageLibrarySource {
	Upload = 'Upload',
	Stock = 'Stock',
	AIGenerated = 'AIGenerated',
}

export enum LibraryItemKind {
	Image = 'Image',
	Document = 'Document',
}

export interface IFileAttachmentMetadata {
	id: string;
	fileName: string;
	fileSize: number;
}

/**
 * An image in the account's reusable library. `id` is the library entry, NOT the attachment: removing an
 * entry deletes only this record, and the attachment it points at keeps working for content that already
 * uses it.
 */
export interface IImageLibraryItem {
	id: string;
	creationDate?: string;
	fileAttachment: IFileAttachmentMetadata;
	/** Caption the user gave the image. Falls back to `fileAttachment.fileName` when absent. */
	displayName?: string;
	source: ImageLibrarySource;
	kind?: LibraryItemKind;
	/** Folders the entry is filed under. An entry can be in several at once. */
	keywords?: string[];
	mimeType?: string;
	url?: string;
}

/** One folder in use on the account, with the number of entries filed under it. */
export interface IImageLibraryKeyword {
	keyword: string;
	count: number;
}

/** Entries written before documents existed carry no kind, and every one of them is an image. */
export function isLibraryDocument(item: IImageLibraryItem) {
	return item.kind === LibraryItemKind.Document;
}
